import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Eye, Target, Hexagon, TrendingUp, Lightbulb, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedAnalysisPage() {
  const { shareId } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const { data } = await axios.get(`${API}/shared/${shareId}`);
        setAnalysis(data);
      } catch {
        setError("This shared analysis is no longer available.");
      }
      setLoading(false);
    };
    fetchShared();
  }, [shareId]);

  if (loading) return (
    <div className="min-h-screen bg-[#F8F9FC] flex items-center justify-center">
      <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F8F9FC] flex items-center justify-center">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center max-w-md">
        <p className="text-lg font-bold text-slate-800 mb-2">Analysis Not Found</p>
        <p className="text-sm text-slate-500">{error}</p>
        <a href="/" className="mt-4 inline-block text-sm text-indigo-600 hover:underline">Go to AdBit</a>
      </div>
    </div>
  );

  const r = analysis.results;
  if (!r) return null;

  const scoreColor = r.neuro_score >= 80 ? "text-emerald-500" : r.neuro_score >= 50 ? "text-amber-500" : "text-red-500";
  const ringColor = r.neuro_score >= 80 ? "#10B981" : r.neuro_score >= 50 ? "#F59E0B" : "#EF4444";

  return (
    <div className="min-h-screen bg-[#F8F9FC]">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
              <span className="text-white font-bold text-xs">N</span>
            </div>
            <span className="font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>AdBit</span>
          </div>
          <Badge variant="secondary" className="text-xs">Shared Report</Badge>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Top section */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {analysis.image_base64 && (
              <div className="w-full md:w-48 flex-shrink-0">
                <img src={analysis.image_base64} alt={analysis.ad_name} className="w-full rounded-xl object-cover border border-slate-200" />
              </div>
            )}
            <div className="flex-1 flex flex-col md:flex-row justify-between gap-6">
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>{analysis.ad_name}</h1>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="secondary" className="text-xs">{analysis.category}</Badge>
                  <Badge variant="secondary" className="text-xs">{analysis.platform}</Badge>
                  <Badge variant="secondary" className="text-xs">{analysis.objective}</Badge>
                </div>
                <p className="text-xs text-slate-400 mt-2">Analyzed {new Date(analysis.created_at).toLocaleString()}</p>
              </div>
              <div className="text-center flex-shrink-0">
                <div className="relative w-32 h-32 mx-auto">
                  <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="#E2E8F0" strokeWidth="8" />
                    <circle cx="60" cy="60" r="52" fill="none" stroke={ringColor} strokeWidth="8"
                      strokeDasharray={`${(r.neuro_score / 100) * 326.7} 326.7`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-3xl font-bold ${scoreColor}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>{r.neuro_score}</span>
                    <span className="text-[10px] text-slate-400">/ 100</span>
                  </div>
                </div>
                <p className="text-sm font-bold mt-1" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Ad Neuro Score</p>
                <Badge className={`mt-1 ${r.neuro_score >= 80 ? "bg-emerald-100 text-emerald-700" : r.neuro_score >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"} border-0`}>
                  {r.score_label}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Neuro Breakdown */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-base font-bold mb-4" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
            <Hexagon size={16} className="inline mr-2 text-indigo-500" />Neuro Score Breakdown
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(r.neuro_breakdown || {}).map(([key, dim]) => {
              const sc = dim.score || 0;
              const color = sc >= 70 ? "text-emerald-600 bg-emerald-50" : sc >= 40 ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50";
              return (
                <div key={key} className="border border-slate-100 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-700">{key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color}`}>{sc}/100</span>
                  </div>
                  <p className="text-xs text-slate-500">{dim.finding}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Engagement */}
        {r.engagement && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-base font-bold mb-4" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
              <TrendingUp size={16} className="inline mr-2 text-indigo-500" />Engagement Prediction
            </h2>
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="bg-slate-50 rounded-lg px-4 py-2"><span className="text-xs text-slate-500">Level</span><p className="text-sm font-bold text-slate-800">{r.engagement.level}</p></div>
              <div className="bg-slate-50 rounded-lg px-4 py-2"><span className="text-xs text-slate-500">Percentile</span><p className="text-sm font-bold text-slate-800">{r.engagement.percentile}</p></div>
              <div className="bg-slate-50 rounded-lg px-4 py-2"><span className="text-xs text-slate-500">Platform Fit</span><p className="text-sm font-bold text-slate-800">{r.engagement.platform_fit_score}/100</p></div>
              <div className="bg-slate-50 rounded-lg px-4 py-2"><span className="text-xs text-slate-500">Predicted CTR</span><p className="text-sm font-bold text-slate-800">{r.engagement.predicted_ctr}</p></div>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {r.recommendations && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-base font-bold mb-4" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
              <Lightbulb size={16} className="inline mr-2 text-indigo-500" />AI Recommendations
            </h2>
            <div className="space-y-3">
              {r.recommendations.map((rec, i) => (
                <div key={i} className="border border-slate-100 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-xs font-bold text-indigo-500 mt-0.5">{i + 1}</span>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-slate-800">{rec.title}</span>
                        <Badge variant="secondary" className="text-[10px]">{rec.priority}</Badge>
                      </div>
                      <p className="text-xs text-slate-500">{rec.description}</p>
                      <div className="flex gap-3 mt-2">
                        <span className="text-[10px] text-indigo-500">Impact: {rec.expected_impact}</span>
                        <span className="text-[10px] text-slate-400">Effort: {rec.effort}</span>
                        <span className="text-[10px] text-slate-400">Principle: {rec.principle}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6">
          <p className="text-xs text-slate-400">Report generated by <a href="/" className="text-indigo-500 hover:underline">AdBit</a></p>
          <p className="text-[10px] text-slate-300 mt-1">Predictions based on computational neuroscience models. Results are predictions, not guarantees.</p>
        </div>
      </div>
    </div>
  );
}
